import { produce } from "immer";
import { useStore } from "jotai";
import { type FC, useCallback, useEffect, useRef } from "react";
import { audioEngine } from "$/modules/audio/audio-engine";
import {
	findNextWord,
	getCurrentLineLocation,
	getCurrentLocation,
	isSynchronizableLine,
	isSynchronizableWord,
} from "$/modules/lyric-editor/utils/lyric-states";
import {
	SyncJudgeMode,
	smartFirstWordAtom,
	smartLastWordAtom,
	syncJudgeModeAtom,
} from "$/modules/settings/states";
import {
	currentEmptyBeatAtom,
	smartFirstWordActiveIdAtom,
	syncTimeOffsetAtom,
	syncTargetModeAtom,
} from "$/modules/settings/states/sync";
import {
	keyMoveNextLineAtom,
	keyMoveNextWordAndPlayAtom,
	keyMoveNextWordAtom,
	keyMovePrevLineAtom,
	keyMovePrevWordAndPlayAtom,
	keyMovePrevWordAtom,
	keyMoveLastWordAndPlayAtom,
	keyMoveFirstWordAndPlayAtom,
	keySyncEndAtom,
	keySyncNextAtom,
	keySyncStartAtom,
	keySyncStartOnlyAtom,
	keyLineSyncHoldAtom,
	keyLineSyncHoldAltAtom,
} from "$/states/keybindings.ts";
import {
	lyricLinesAtom,
	selectedLinesAtom,
	selectedWordsAtom,
} from "$/states/main.ts";
import {
	type KeyBindingEvent,
	useKeyBindingAtom,
	isEditing,
} from "$/utils/keybindings.ts";

export const SyncKeyBinding: FC = () => {
	const store = useStore();

	const calcJudgeTime = useCallback(
		(evt: KeyBindingEvent) => {
			const syncTimeOffset = store.get(syncTimeOffsetAtom);
			const currentTime = Math.max(
				0,
				audioEngine.musicCurrentTime * 1000 + syncTimeOffset,
			);
			const syncJudgeMode = store.get(syncJudgeModeAtom);
			if (syncJudgeMode === SyncJudgeMode.FirstKeyDownTimeLegacy) {
				return (
					Math.max(
						0,
						audioEngine.musicCurrentTime * 1000 -
						evt.downTimeOffset +
						syncTimeOffset,
					) | 0
				);
			}
			let timeAdjustment = 0;
			if (audioEngine.musicPlaying) {
				switch (syncJudgeMode) {
					case SyncJudgeMode.FirstKeyDownTime:
						timeAdjustment -= evt.downTimeOffset;
						break;
					case SyncJudgeMode.LastKeyUpTime:
						break;
					case SyncJudgeMode.MiddleKeyTime:
						timeAdjustment -= currentTime - evt.downTimeOffset / 2;
						break;
				}
				timeAdjustment *= audioEngine.musicPlayBackRate;
			}
			return Math.max(0, currentTime + timeAdjustment) | 0;
		},
		[store],
	);

	const moveToNextWordBase = useCallback(
		(play: boolean): boolean => {
			const location = getCurrentLocation(store);
			if (!location) return false;
			const nextWord = findNextWord(
				location.lines,
				location.lineIndex,
				location.wordIndex,
			);
			if (!nextWord) return false;
			store.set(selectedWordsAtom, new Set([nextWord.word.id]));
			store.set(selectedLinesAtom, new Set([nextWord.line.id]));
			store.set(currentEmptyBeatAtom, 0);
			if (play) audioEngine.seekMusic(nextWord.word.startTime / 1000);
			return true;
		},
		[store],
	);

	const moveToNextWord = useCallback(
		() => moveToNextWordBase(false),
		[moveToNextWordBase],
	);
	const moveToNextWordAndPlay = useCallback(
		() => moveToNextWordBase(true),
		[moveToNextWordBase],
	);

	const moveToPrevWordBase = useCallback(
		(play: boolean): boolean => {
			const location = getCurrentLocation(store);
			if (!location) return false;
			if (location.wordIndex === 0) {
				if (location.lineIndex === 0) return false;
				const lastLineIndex = Math.max(0, location.lineIndex);
				const lastLine = location.lines
					.slice(0, lastLineIndex)
					.reverse()
					.find(isSynchronizableLine);
				if (!lastLine) return false;
				store.set(selectedLinesAtom, new Set([lastLine.id]));
				if (lastLine.words.length === 0) {
					store.set(selectedWordsAtom, new Set());
				} else {
					const lastWord = lastLine.words
						.slice()
						.reverse()
						.find(isSynchronizableWord);
					if (!lastWord) return false;
					store.set(selectedWordsAtom, new Set([lastWord.id]));
					if (play) audioEngine.seekMusic(lastWord.startTime / 1000);
				}
			} else {
				const prevWord = location.line.words
					.slice(0, location.wordIndex)
					.reverse()
					.find(isSynchronizableWord);
				if (!prevWord) return false;
				store.set(selectedWordsAtom, new Set([prevWord.id]));
				if (play) audioEngine.seekMusic(prevWord.startTime / 1000);
			}
			return true;
		},
		[store],
	);
	const moveToPrevWord = useCallback(
		() => moveToPrevWordBase(false),
		[moveToPrevWordBase],
	);
	const moveToPrevWordAndPlay = useCallback(
		() => moveToPrevWordBase(true),
		[moveToPrevWordBase],
	);

	// Helper: move selection to next line's first word
	const moveToNextLine = useCallback(() => {
		const location = getCurrentLineLocation(store);
		if (!location) return;
		const nextLine = location.lines
			.slice(location.lineIndex + 1)
			.find(isSynchronizableLine);
		if (!nextLine) return;
		store.set(selectedLinesAtom, new Set([nextLine.id]));
		if (nextLine.words.length === 0) {
			store.set(selectedWordsAtom, new Set());
		} else {
			const firstWord = nextLine.words.find(isSynchronizableWord);
			if (firstWord) {
				store.set(selectedWordsAtom, new Set([firstWord.id]));
			} else {
				store.set(selectedWordsAtom, new Set());
			}
		}
	}, [store]);

	// 移动打轴光标

	useKeyBindingAtom(keyMoveNextLineAtom, () => {
		const location = getCurrentLineLocation(store);
		if (!location) return;
		const lastLineIndex = Math.min(
			location.lines.length,
			location.lineIndex + 1,
		);
		const lastLine = location.lines[lastLineIndex];
		if (!lastLine) return;
		store.set(selectedLinesAtom, new Set([lastLine.id]));
		if (lastLine.words.length === 0) {
			store.set(selectedWordsAtom, new Set());
		} else {
			store.set(selectedWordsAtom, new Set([lastLine.words[0]?.id]));
		}
	}, [store]);

	useKeyBindingAtom(keyMovePrevLineAtom, () => {
		const location = getCurrentLineLocation(store);
		if (!location) return;
		const lastLineIndex = Math.max(0, location.lineIndex - 1);
		const lastLine = location.lines[lastLineIndex];
		if (!lastLine) return;
		store.set(selectedLinesAtom, new Set([lastLine.id]));
		if (lastLine.words.length === 0) {
			store.set(selectedWordsAtom, new Set());
		} else {
			store.set(selectedWordsAtom, new Set([lastLine.words[0]?.id]));
		}
	}, [store]);

	useKeyBindingAtom(keyMoveNextWordAtom, moveToNextWord, [store]);
	useKeyBindingAtom(keyMoveNextWordAndPlayAtom, moveToNextWordAndPlay, [store]);
	useKeyBindingAtom(keyMovePrevWordAtom, moveToPrevWord, [store]);
	useKeyBindingAtom(keyMovePrevWordAndPlayAtom, moveToPrevWordAndPlay, [store]);

	useKeyBindingAtom(keyMoveLastWordAndPlayAtom, () => {
		const location = getCurrentLineLocation(store);
		if (!location) return;

		const lastWord = location.line.words
			.slice()
			.reverse()
			.find(isSynchronizableWord);

		if (!lastWord) return;

		store.set(selectedWordsAtom, new Set([lastWord.id]));
		store.set(selectedLinesAtom, new Set([location.line.id]));
		audioEngine.seekMusic(lastWord.startTime / 1000);
	}, [store]);

	useKeyBindingAtom(keyMoveFirstWordAndPlayAtom, () => {
		const location = getCurrentLineLocation(store);
		if (!location) return;

		const firstWord = location.line.words.find(isSynchronizableWord);
		if (!firstWord) return;

		store.set(selectedWordsAtom, new Set([firstWord.id]));
		store.set(selectedLinesAtom, new Set([location.line.id]));
		audioEngine.seekMusic(firstWord.startTime / 1000);
	}, [store]);

	// 记录时间戳（主要打轴按键）

	// C key: Mark begin + auto-advance to next word
	useKeyBindingAtom(
		keySyncStartAtom,
		(evt) => {
			if (store.get(syncTargetModeAtom) !== "word") return;
			const location = getCurrentLocation(store);
			if (!location) return;
			const currentTime = calcJudgeTime(evt);

			const smartFirstWord = store.get(smartFirstWordAtom);
			if (smartFirstWord && location.isFirstWord) {
				store.set(smartFirstWordActiveIdAtom, location.word.id);
			}

			store.set(lyricLinesAtom, (state) =>
				produce(state, (state) => {
					const line = state.lyricLines[location.lineIndex];
					if (location.isFirstWord) {
						line.startTime = currentTime;
					}
					line.words[location.wordIndex].startTime = currentTime;

					// Auto-set previous synchronizable word's end time to current time
					const prevSyncWord = line.words
						.slice(0, location.wordIndex)
						.reverse()
						.find((w) => w.word.trim().length > 0);
					if (prevSyncWord) {
						prevSyncWord.endTime = currentTime;
					} else if (location.lineIndex > 0) {
						// No previous sync word on this line — end the last word of previous line
						const prevLine = state.lyricLines[location.lineIndex - 1];
						if (prevLine && prevLine.words.length > 0) {
							const prevLineLastWord = prevLine.words
								.slice()
								.reverse()
								.find((w) => w.word.trim().length > 0);
							if (prevLineLastWord) {
								prevLineLastWord.endTime = currentTime;
							}
							prevLine.endTime = currentTime;
						}
					}
				}),
			);

			// Auto-advance to next word
			moveToNextWord();
		},
		[store, moveToNextWord],
	);

	// F key: Mark begin only (no auto-advance)
	useKeyBindingAtom(
		keySyncStartOnlyAtom,
		(evt) => {
			if (store.get(syncTargetModeAtom) !== "word") return;
			const location = getCurrentLocation(store);
			if (!location) return;
			const currentTime = calcJudgeTime(evt);

			const smartFirstWord = store.get(smartFirstWordAtom);
			if (smartFirstWord && location.isFirstWord) {
				store.set(smartFirstWordActiveIdAtom, location.word.id);
			}

			store.set(lyricLinesAtom, (state) =>
				produce(state, (state) => {
					const line = state.lyricLines[location.lineIndex];
					if (location.isFirstWord) {
						line.startTime = currentTime;
					}
					line.words[location.wordIndex].startTime = currentTime;
				}),
			);
		},
		[store],
	);
	useKeyBindingAtom(
		keySyncNextAtom,
		(evt) => {
			if (store.get(syncTargetModeAtom) !== "word") return;
			const location = getCurrentLocation(store);
			if (!location) return;
			const currentTime = calcJudgeTime(evt);

			// 智能首字
			const smartFirstWord = store.get(smartFirstWordAtom);
			if (smartFirstWord && location.isFirstWord) {
				const activeId = store.get(smartFirstWordActiveIdAtom);
				if (activeId !== location.word.id) {
					store.set(lyricLinesAtom, (state) =>
						produce(state, (state) => {
							const line = state.lyricLines[location.lineIndex];
							line.startTime = currentTime;
							line.words[location.wordIndex].startTime = currentTime;
						}),
					);
					store.set(smartFirstWordActiveIdAtom, location.word.id);
					return;
				}
			}
			store.set(smartFirstWordActiveIdAtom, null);

			const emptyBeat = store.get(currentEmptyBeatAtom);
			if (emptyBeat < location.word.emptyBeat) {
				store.set(currentEmptyBeatAtom, emptyBeat + 1);
				return;
			}

			// 智能尾字
			const smartLastWord = store.get(smartLastWordAtom);
			if (smartLastWord && location.isLastWord) {
				store.set(lyricLinesAtom, (state) =>
					produce(state, (state) => {
						const line = state.lyricLines[location.lineIndex];
						line.words[location.wordIndex].endTime = currentTime;
						line.endTime = currentTime;
					}),
				);
				moveToNextWord();
				return;
			}

			store.set(lyricLinesAtom, (state) =>
				produce(state, (state) => {
					const curLine = state.lyricLines[location.lineIndex];
					curLine.words[location.wordIndex].endTime = currentTime;
					const nextWord = findNextWord(
						state.lyricLines,
						location.lineIndex,
						location.wordIndex,
					);
					if (nextWord) {
						if (curLine !== nextWord.line) {
							curLine.endTime = currentTime;
							nextWord.line.startTime = currentTime;
						}
						nextWord.word.startTime = currentTime;
					}
				}),
			);
			moveToNextWord();

			// 开了智能首字后，连轴打到下一行时跳过智能首字
			if (smartFirstWord) {
				const newLocation = getCurrentLocation(store);
				if (newLocation?.isFirstWord) {
					store.set(smartFirstWordActiveIdAtom, newLocation.word.id);
				}
			}
		},
		[store, moveToNextWord],
	);
	useKeyBindingAtom(
		keySyncEndAtom,
		(evt) => {
			if (store.get(syncTargetModeAtom) !== "word") return;
			const location = getCurrentLocation(store);
			if (!location) return;
			const currentTime = calcJudgeTime(evt);
			store.set(lyricLinesAtom, (state) =>
				produce(state, (state) => {
					const line = state.lyricLines[location.lineIndex];
					line.words[location.wordIndex].endTime = currentTime;
					if (location.isLastWord) {
						line.endTime = currentTime;
					}
				}),
			);
			moveToNextWord();
		},
		[store, moveToNextWord],
	);

	// =====================================================================
	// Line-by-line sync hold keys (J / K)
	// Hold key down = line start, release = line end, auto-advance to next line
	// =====================================================================
	const lineSyncDownTimeRef = useRef<number | null>(null);
	const lineSyncActiveKeyRef = useRef<string | null>(null);

	useEffect(() => {
		const getLineSyncKeys = (): string[][] => {
			const holdKeys = store.get(keyLineSyncHoldAtom);
			const holdAltKeys = store.get(keyLineSyncHoldAltAtom);
			return [holdKeys, holdAltKeys];
		};

		// Normalize a key code similar to removeSideOfKeyCode in keybindings.ts
		const normalizeCode = (code: string) => {
			const blackListed = ["Bracket", "Arrow"];
			for (const key of blackListed) {
				if (code.startsWith(key)) return code;
			}
			if (code.endsWith("Left")) return code.substring(0, code.length - 4);
			if (code.endsWith("Right")) return code.substring(0, code.length - 5);
			return code;
		};

		const matchesBinding = (code: string, binding: string[]): boolean => {
			// For a single-key binding, just check if it matches
			if (binding.length === 1) {
				return normalizeCode(code) === binding[0];
			}
			return false;
		};

		const handleKeyDown = (evt: KeyboardEvent) => {
			if (store.get(syncTargetModeAtom) !== "line") return;
			if (evt.repeat) return;
			if (isEditing(evt)) return;
			if (lineSyncDownTimeRef.current !== null) return; // already holding

			const code = normalizeCode(evt.code);
			const [holdKeys, holdAltKeys] = getLineSyncKeys();

			if (!matchesBinding(code, holdKeys) && !matchesBinding(code, holdAltKeys)) {
				return;
			}

			// Record the start time
			const syncTimeOffset = store.get(syncTimeOffsetAtom);
			const startTime = Math.max(0, audioEngine.musicCurrentTime * 1000 + syncTimeOffset) | 0;
			lineSyncDownTimeRef.current = startTime;
			lineSyncActiveKeyRef.current = code;

			// Set line start time immediately
			const location = getCurrentLineLocation(store);
			if (!location) return;

			store.set(lyricLinesAtom, (state) =>
				produce(state, (state) => {
					const line = state.lyricLines[location.lineIndex];
					line.startTime = startTime;
				}),
			);

			evt.preventDefault();
			evt.stopPropagation();
		};

		const handleKeyUp = (evt: KeyboardEvent) => {
			if (lineSyncDownTimeRef.current === null) return;

			const code = normalizeCode(evt.code);
			if (code !== lineSyncActiveKeyRef.current) return;

			// Record the end time
			const syncTimeOffset = store.get(syncTimeOffsetAtom);
			const endTime = Math.max(0, audioEngine.musicCurrentTime * 1000 + syncTimeOffset) | 0;
			const startTime = lineSyncDownTimeRef.current;

			lineSyncDownTimeRef.current = null;
			lineSyncActiveKeyRef.current = null;

			if (endTime <= startTime) return;

			const location = getCurrentLineLocation(store);
			if (!location) return;

			// Set line end time (don't touch word times)
			store.set(lyricLinesAtom, (state) =>
				produce(state, (state) => {
					const line = state.lyricLines[location.lineIndex];
					line.endTime = endTime;
				}),
			);

			// Auto-advance to next line
			moveToNextLine();

			evt.preventDefault();
			evt.stopPropagation();
		};

		window.addEventListener("keydown", handleKeyDown);
		window.addEventListener("keyup", handleKeyUp);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("keyup", handleKeyUp);
		};
	}, [store, moveToNextLine]);

	return null;
};
